import React, { useState, useEffect } from 'react';
import { 
  LogIn, LogOut, LayoutDashboard, Clock, TrendingUp, Shield, 
  Plus, Edit, Trash2, Save, X, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Smartphone, Key, Loader2,
  BarChart3, Users, Eye, Globe, ArrowUpRight, ArrowDownRight,
  Settings, Lock, RefreshCw, QrCode, Download, Database, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Toast notification component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
    type === 'success' ? 'bg-green-500' : type === 'warning' ? 'bg-amber-500' : 'bg-red-500'
  } text-white animate-slideIn`}>
    {type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
    <span className="text-sm font-medium">{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-80">
      <X size={16} />
    </button>
  </div>
);

// 2FA Setup Component
const TwoFactorSetup = ({ token, onComplete, showToast }) => {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQRCode();
  }, []);

  const fetchQRCode = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/setup-2fa`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        setQrCode(data.qr_code);
        setSecret(data.secret);
      } else {
        setError(data.detail || 'Failed to generate QR code');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ otp_code: otpCode }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showToast('2FA enabled successfully!', 'success');
        // Store the new full-access token
        localStorage.setItem('adminToken', data.token);
        onComplete(data.token);
      } else {
        setError(data.detail || 'Invalid verification code');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Shield size={28} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Set Up 2-Factor Authentication</h1>
            <p className="text-slate-500 text-sm mt-1">Secure your admin account with Google Authenticator</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 size={32} className="text-amber-500 animate-spin" />
              <p className="text-slate-500 text-sm mt-3">Generating QR code...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Step 1: Scan QR Code */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-slate-700">
                  <span className="w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">1</span>
                  <span className="font-medium">Scan with Google Authenticator</span>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 flex justify-center">
                  {qrCode && (
                    <img 
                      src={qrCode} 
                      alt="2FA QR Code" 
                      className="w-48 h-48 rounded-lg"
                      data-testid="qr-code-image"
                    />
                  )}
                </div>
              </div>

              {/* Manual Entry Option */}
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-2">Can't scan? Enter this code manually:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded-lg text-sm font-mono text-slate-700 border border-slate-200 break-all">
                    {secret}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(secret);
                      showToast('Secret copied to clipboard', 'success');
                    }}
                    className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
                    data-testid="copy-secret-btn"
                  >
                    <Key size={16} />
                  </button>
                </div>
              </div>

              {/* Step 2: Verify Code */}
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="flex items-center gap-2 text-slate-700">
                  <span className="w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">2</span>
                  <span className="font-medium">Enter verification code</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-center text-xl font-mono tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    required
                    data-testid="otp-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={verifying || otpCode.length !== 6}
                  className="w-full py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="verify-2fa-btn"
                >
                  {verifying ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Verify & Enable 2FA
                    </>
                  )}
                </button>
              </form>

              <p className="text-xs text-slate-400 text-center">
                <Smartphone size={12} className="inline mr-1" />
                Download Google Authenticator from your app store if you haven't already
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Login Component with 2FA support
const LoginForm = ({ onLogin, onSetup2FA }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const body = { email, password };
      if (requires2FA && otpCode) {
        body.otp_code = otpCode;
      }

      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.status === 429) {
        setError(data.detail || 'Too many login attempts. Please try again later.');
        return;
      }

      if (response.ok) {
        if (data.requires_2fa_setup) {
          // Need to set up 2FA first
          localStorage.setItem('adminToken', data.token);
          onSetup2FA(data.token);
        } else if (data.requires_2fa) {
          // Need to enter OTP
          setRequires2FA(true);
        } else if (data.token) {
          // Full login success
          localStorage.setItem('adminToken', data.token);
          onLogin(data.token);
        }
      } else {
        setError(data.detail || 'Login failed');
        if (requires2FA) {
          setOtpCode(''); // Clear OTP on failure
        }
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <LogIn size={28} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Admin Login</h1>
            <p className="text-slate-500 text-sm mt-1">
              {requires2FA ? 'Enter your verification code' : 'Manage your portfolio content'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!requires2FA ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                    placeholder="admin@example.com"
                    required
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                </div>
              </>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={18} className="text-amber-500" />
                  <label className="text-sm font-medium text-slate-700">Verification Code</label>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Enter the 6-digit code from your Google Authenticator app
                </p>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-center text-xl font-mono tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  required
                  data-testid="login-otp-input"
                />
                <button
                  type="button"
                  onClick={() => {
                    setRequires2FA(false);
                    setOtpCode('');
                    setError('');
                  }}
                  className="mt-3 text-sm text-slate-500 hover:text-amber-600 transition-colors"
                >
                  ← Back to login
                </button>
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || (requires2FA && otpCode.length !== 6)}
              className="w-full py-3 bg-slate-800 text-white font-semibold rounded-xl hover:bg-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {requires2FA ? 'Verifying...' : 'Signing in...'}
                </>
              ) : (
                <>
                  {requires2FA ? <Shield size={18} /> : <LogIn size={18} />}
                  {requires2FA ? 'Verify' : 'Sign In'}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
              <Shield size={12} />
              Protected by 2-Factor Authentication
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Timeline Manager Component
const TimelineManager = ({ token, showToast }) => {
  const [events, setEvents] = useState({ professional: [], investments: [], personal: [] });
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [expandedTrack, setExpandedTrack] = useState('professional');

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/timeline`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setEvents(data.data);
      }
    } catch (err) {
      showToast('Failed to fetch timeline', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleSave = async (event, isNew = false) => {
    try {
      const url = isNew 
        ? `${API_URL}/api/admin/timeline`
        : `${API_URL}/api/admin/timeline/${event._id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(event),
      });

      if (response.ok) {
        showToast(isNew ? 'Event created!' : 'Event updated!', 'success');
        fetchEvents();
        setEditingEvent(null);
      } else {
        showToast('Failed to save event', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/timeline/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        showToast('Event deleted!', 'success');
        fetchEvents();
      } else {
        showToast('Failed to delete event', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    }
  };

  const tracks = [
    { key: 'professional', label: 'Professional', color: 'bg-slate-500' },
    { key: 'investments', label: 'Investments', color: 'bg-amber-500' },
    { key: 'personal', label: 'Personal', color: 'bg-rose-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={32} className="text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-800">Timeline Events</h2>
        <button
          onClick={() => setEditingEvent({ year: '', track: 'professional', title: '', description: '', is_published: true })}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all text-sm font-medium"
          data-testid="add-event-btn"
        >
          <Plus size={16} />
          Add Event
        </button>
      </div>

      {/* Event Editor Modal */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">
                {editingEvent._id ? 'Edit Event' : 'New Event'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                  <input
                    type="text"
                    value={editingEvent.year || ''}
                    onChange={(e) => setEditingEvent({ ...editingEvent, year: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    placeholder="2024"
                    data-testid="event-year-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Track</label>
                  <select
                    value={editingEvent.track || 'professional'}
                    onChange={(e) => setEditingEvent({ ...editingEvent, track: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    data-testid="event-track-select"
                  >
                    <option value="professional">Professional</option>
                    <option value="investments">Investments</option>
                    <option value="personal">Personal</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editingEvent.title || ''}
                  onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="Event title"
                  data-testid="event-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subtitle/Company</label>
                <input
                  type="text"
                  value={editingEvent.subtitle || editingEvent.company || ''}
                  onChange={(e) => setEditingEvent({ ...editingEvent, subtitle: e.target.value, company: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="Company or subtitle"
                  data-testid="event-company-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={editingEvent.description || ''}
                  onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                  rows={3}
                  placeholder="Event description..."
                  data-testid="event-description-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={editingEvent.is_published !== false}
                  onChange={(e) => setEditingEvent({ ...editingEvent, is_published: e.target.checked })}
                  className="w-4 h-4 text-amber-500 rounded"
                  data-testid="event-published-checkbox"
                />
                <label htmlFor="published" className="text-sm text-slate-700">Published</label>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setEditingEvent(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all text-sm font-medium"
                data-testid="cancel-event-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSave(editingEvent, !editingEvent._id)}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all text-sm font-medium flex items-center gap-2"
                data-testid="save-event-btn"
              >
                <Save size={16} />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Track Accordions */}
      {tracks.map((track) => (
        <div key={track.key} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <button
            onClick={() => setExpandedTrack(expandedTrack === track.key ? null : track.key)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
            data-testid={`track-${track.key}-btn`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${track.color}`} />
              <span className="font-semibold text-slate-800">{track.label}</span>
              <span className="text-xs text-slate-400">({events[track.key]?.length || 0} events)</span>
            </div>
            {expandedTrack === track.key ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {expandedTrack === track.key && (
            <div className="border-t border-slate-200">
              {events[track.key]?.length === 0 ? (
                <p className="p-4 text-slate-500 text-sm text-center">No events</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {events[track.key]?.map((event) => (
                    <div key={event._id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-amber-600">{event.year}</span>
                          <span className="font-medium text-slate-800 truncate">{event.title}</span>
                          {!event.is_published && (
                            <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Draft</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{event.description}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => setEditingEvent(event)}
                          className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
                          data-testid={`edit-event-${event._id}`}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(event._id)}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                          data-testid={`delete-event-${event._id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Investments Manager Component
const InvestmentsManager = ({ token, showToast }) => {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingInvestment, setEditingInvestment] = useState(null);

  const fetchInvestments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/investments`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setInvestments(data.data);
      }
    } catch (err) {
      showToast('Failed to fetch investments', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestments();
  }, []);

  const handleSave = async (investment, isNew = false) => {
    try {
      const url = isNew 
        ? `${API_URL}/api/admin/investments`
        : `${API_URL}/api/admin/investments/${investment._id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(investment),
      });

      if (response.ok) {
        showToast(isNew ? 'Investment created!' : 'Investment updated!', 'success');
        fetchInvestments();
        setEditingInvestment(null);
      } else {
        showToast('Failed to save investment', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this investment?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/investments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        showToast('Investment deleted!', 'success');
        fetchInvestments();
      } else {
        showToast('Failed to delete investment', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={32} className="text-amber-500 animate-spin" />
      </div>
    );
  }

  const angelInvestments = investments.filter(i => i.type === 'angel');
  const crowdfunding = investments.filter(i => i.type === 'crowdfunding');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-800">Investments</h2>
        <button
          onClick={() => setEditingInvestment({ name: '', sector: '', type: 'angel', description: '', url: '' })}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all text-sm font-medium"
          data-testid="add-investment-btn"
        >
          <Plus size={16} />
          Add Investment
        </button>
      </div>

      {/* Investment Editor Modal */}
      {editingInvestment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">
                {editingInvestment._id ? 'Edit Investment' : 'New Investment'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={editingInvestment.name || ''}
                  onChange={(e) => setEditingInvestment({ ...editingInvestment, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="Company name"
                  data-testid="investment-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sector</label>
                  <input
                    type="text"
                    value={editingInvestment.sector || ''}
                    onChange={(e) => setEditingInvestment({ ...editingInvestment, sector: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    placeholder="FinTech, HealthTech..."
                    data-testid="investment-sector-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select
                    value={editingInvestment.type || 'angel'}
                    onChange={(e) => setEditingInvestment({ ...editingInvestment, type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    data-testid="investment-type-select"
                  >
                    <option value="angel">Angel Investment</option>
                    <option value="crowdfunding">Crowdfunding</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={editingInvestment.description || ''}
                  onChange={(e) => setEditingInvestment({ ...editingInvestment, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                  rows={2}
                  placeholder="Brief description..."
                  data-testid="investment-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Website URL</label>
                <input
                  type="url"
                  value={editingInvestment.url || ''}
                  onChange={(e) => setEditingInvestment({ ...editingInvestment, url: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="https://..."
                  data-testid="investment-url-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Logo URL (optional)</label>
                <input
                  type="url"
                  value={editingInvestment.logo || ''}
                  onChange={(e) => setEditingInvestment({ ...editingInvestment, logo: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="https://..."
                  data-testid="investment-logo-input"
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setEditingInvestment(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all text-sm font-medium"
                data-testid="cancel-investment-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSave(editingInvestment, !editingInvestment._id)}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all text-sm font-medium flex items-center gap-2"
                data-testid="save-investment-btn"
              >
                <Save size={16} />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Angel Investments */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Angel Investments ({angelInvestments.length})</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {angelInvestments.length === 0 ? (
            <p className="p-4 text-slate-500 text-sm text-center">No angel investments</p>
          ) : (
            angelInvestments.map((inv) => (
              <div key={inv._id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-xs font-bold">
                    {inv.name?.charAt(0)}
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">{inv.name}</span>
                    <span className="text-xs text-amber-600 ml-2">{inv.sector}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingInvestment(inv)}
                    className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
                    data-testid={`edit-investment-${inv._id}`}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(inv._id)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    data-testid={`delete-investment-${inv._id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Crowdfunding */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Crowdfunding ({crowdfunding.length})</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {crowdfunding.length === 0 ? (
            <p className="p-4 text-slate-500 text-sm text-center">No crowdfunding investments</p>
          ) : (
            crowdfunding.map((inv) => (
              <div key={inv._id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div>
                  <span className="font-medium text-slate-800">{inv.name}</span>
                  <span className="text-xs text-slate-500 ml-2">{inv.sector}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingInvestment(inv)}
                    className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
                    data-testid={`edit-crowdfund-${inv._id}`}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(inv._id)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    data-testid={`delete-crowdfund-${inv._id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Analytics Dashboard Component
const AnalyticsDashboard = ({ token, showToast }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setAnalytics(data.data);
      }
    } catch (err) {
      showToast('Failed to fetch analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={32} className="text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-slate-500">
        No analytics data available yet
      </div>
    );
  }

  const { summary, chart, top_referrers, top_pages } = analytics;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-800">Visitor Analytics</h2>
        <button
          onClick={fetchAnalytics}
          className="flex items-center gap-2 px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg transition-all text-sm"
          data-testid="refresh-analytics-btn"
        >
          <Loader2 size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <Eye size={14} />
            Today
          </div>
          <div className="text-2xl font-bold text-slate-800">{summary.today_views}</div>
          <div className="text-xs text-slate-400">{summary.today_unique} unique visitors</div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <BarChart3 size={14} />
            Last 7 Days
          </div>
          <div className="text-2xl font-bold text-slate-800">{summary.views_7d}</div>
          <div className="text-xs text-slate-400">{summary.unique_7d} unique visitors</div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <Users size={14} />
            Last 30 Days
          </div>
          <div className="text-2xl font-bold text-slate-800">{summary.views_30d}</div>
          <div className="text-xs text-slate-400">{summary.unique_30d} unique visitors</div>
        </div>
      </div>

      {/* Chart - Simple bar representation */}
      {chart && chart.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-800 mb-4">Daily Views (Last 30 Days)</h3>
          <div className="flex items-end gap-1 h-32">
            {chart.slice(-30).map((day, idx) => {
              const maxViews = Math.max(...chart.map(d => d.views), 1);
              const height = (day.views / maxViews) * 100;
              return (
                <div 
                  key={idx} 
                  className="flex-1 bg-amber-500 rounded-t hover:bg-amber-600 transition-colors cursor-pointer group relative"
                  style={{ height: `${Math.max(height, 4)}%` }}
                  title={`${day.date}: ${day.views} views, ${day.visitors} unique`}
                >
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {day.views} views
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-400">
            <span>{chart[0]?.date}</span>
            <span>{chart[chart.length - 1]?.date}</span>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* Top Referrers */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Globe size={16} />
            Top Referrers
          </h3>
          {top_referrers && top_referrers.length > 0 ? (
            <div className="space-y-2">
              {top_referrers.map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 truncate flex-1 mr-2">
                    {ref.url.replace(/^https?:\/\//, '').split('/')[0]}
                  </span>
                  <span className="text-slate-400 font-medium">{ref.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No referrer data yet</p>
          )}
        </div>

        {/* Top Pages */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Eye size={16} />
            Top Pages
          </h3>
          {top_pages && top_pages.length > 0 ? (
            <div className="space-y-2">
              {top_pages.map((page, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 truncate flex-1 mr-2">{page.page || '/'}</span>
                  <span className="text-slate-400 font-medium">{page.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No page data yet</p>
          )}
        </div>
      </div>

      {/* Device & Browser Stats */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Devices */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Smartphone size={16} />
            Devices (7 days)
          </h3>
          {analytics.devices && analytics.devices.length > 0 ? (
            <div className="space-y-2">
              {analytics.devices.map((d, idx) => {
                const total = analytics.devices.reduce((sum, x) => sum + x.count, 0);
                const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600 capitalize">{d.device}</span>
                        <span className="text-slate-400">{pct}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-amber-500 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No device data yet</p>
          )}
        </div>

        {/* Browsers */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Globe size={16} />
            Browsers (7 days)
          </h3>
          {analytics.browsers && analytics.browsers.length > 0 ? (
            <div className="space-y-2">
              {analytics.browsers.map((b, idx) => {
                const total = analytics.browsers.reduce((sum, x) => sum + x.count, 0);
                const pct = total > 0 ? Math.round((b.count / total) * 100) : 0;
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600 capitalize">{b.browser}</span>
                        <span className="text-slate-400">{pct}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-slate-500 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No browser data yet</p>
          )}
        </div>
      </div>

      {/* Privacy Note */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-emerald-800 text-sm">Privacy Compliant Analytics</h4>
            <p className="text-xs text-emerald-600 mt-1">
              {analytics.privacy_note || "All data is anonymized. IPs are double-hashed. No cookies or personal data stored. Auto-deletes after 90 days. GDPR/CCPA compliant."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Settings Manager Component
const SettingsManager = ({ token, showToast }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // JWT rotation
  const [newJwtSecret, setNewJwtSecret] = useState('');
  const [showJwtSecret, setShowJwtSecret] = useState(false);
  
  // 2FA reset
  const [show2faReset, setShow2faReset] = useState(false);
  const [newQrCode, setNewQrCode] = useState('');
  const [newSecret, setNewSecret] = useState('');
  const [verifyOtp, setVerifyOtp] = useState('');
  
  // Backup & Seed
  const [backupLoading, setBackupLoading] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedResults, setSeedResults] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch (err) {
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }
    
    if (newPassword.length < 12) {
      showToast('Password must be at least 12 characters', 'error');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showToast('Password changed successfully', 'success');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        fetchSettings();
      } else {
        showToast(data.detail || 'Failed to change password', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRotateJwt = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/rotate-jwt-secret`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setNewJwtSecret(data.new_secret);
        setShowJwtSecret(true);
        showToast('New JWT secret generated', 'success');
      } else {
        showToast(data.detail || 'Failed to generate new secret', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset2fa = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reset-2fa`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setNewQrCode(data.qr_code);
        setNewSecret(data.secret);
        setShow2faReset(true);
        showToast('Scan the new QR code with your authenticator', 'warning');
      } else {
        showToast(data.detail || 'Failed to reset 2FA', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify2faReset = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ otp_code: verifyOtp }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showToast('2FA reset and verified successfully', 'success');
        setShow2faReset(false);
        setNewQrCode('');
        setNewSecret('');
        setVerifyOtp('');
        fetchSettings();
      } else {
        showToast(data.detail || 'Invalid verification code', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', 'success');
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/backup`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Create downloadable JSON file
        const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `portfolio-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast('Backup downloaded successfully', 'success');
      } else {
        showToast(data.detail || 'Backup failed', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('This will seed empty collections with default data. Continue?')) {
      return;
    }
    
    setSeedLoading(true);
    setSeedResults(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSeedResults(data.details);
        showToast(data.message, 'success');
      } else {
        showToast(data.detail || 'Seed failed', 'error');
      }
    } catch (err) {
      showToast('Connection error', 'error');
    } finally {
      setSeedLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Security Status */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Shield size={20} className="text-emerald-500" />
          Security Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500">Email</p>
            <p className="font-medium text-slate-800">{settings?.email}</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500">2FA Status</p>
            <p className={`font-medium ${settings?.two_factor_enabled ? 'text-emerald-600' : 'text-red-600'}`}>
              {settings?.two_factor_enabled ? '✓ Enabled' : '✗ Disabled'}
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500">Password Last Changed</p>
            <p className="font-medium text-slate-800">
              {settings?.password_last_changed 
                ? new Date(settings.password_last_changed).toLocaleDateString() 
                : 'Never'}
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500">JWT Rotation Pending</p>
            <p className={`font-medium ${settings?.pending_jwt_rotation ? 'text-amber-600' : 'text-slate-800'}`}>
              {settings?.pending_jwt_rotation ? 'Yes - Restart Required' : 'No'}
            </p>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Lock size={20} className="text-amber-500" />
          Change Password
        </h3>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">New Password (min 12 chars)</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              minLength={12}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              required
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-all"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Change Password
          </button>
        </form>
      </div>

      {/* Reset 2FA */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <QrCode size={20} className="text-purple-500" />
          Reset 2FA (Authenticator App)
        </h3>
        
        {!show2faReset ? (
          <div>
            <p className="text-sm text-slate-600 mb-4">
              Lost access to your authenticator app? Reset 2FA to generate a new QR code.
            </p>
            <button
              onClick={handleReset2fa}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600 disabled:opacity-50 transition-all"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Reset 2FA
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <strong>Important:</strong> Scan this QR code with your authenticator app, then enter the code below to verify.
            </div>
            
            <div className="flex flex-col items-center">
              <img src={newQrCode} alt="2FA QR Code" className="w-48 h-48 rounded-lg" />
              <div className="mt-2 text-center">
                <p className="text-xs text-slate-500">Manual entry code:</p>
                <code 
                  className="text-sm bg-slate-100 px-2 py-1 rounded cursor-pointer hover:bg-slate-200"
                  onClick={() => copyToClipboard(newSecret)}
                >
                  {newSecret}
                </code>
              </div>
            </div>
            
            <form onSubmit={handleVerify2faReset} className="flex gap-2">
              <input
                type="text"
                value={verifyOtp}
                onChange={(e) => setVerifyOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                pattern="[0-9]{6}"
                required
              />
              <button
                type="submit"
                disabled={saving || verifyOtp.length !== 6}
                className="px-4 py-2 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600 disabled:opacity-50 transition-all"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : 'Verify'}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Rotate JWT Secret */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Key size={20} className="text-blue-500" />
          Rotate JWT Secret
        </h3>
        
        {!showJwtSecret ? (
          <div>
            <p className="text-sm text-slate-600 mb-4">
              Generate a new JWT secret for enhanced security. After updating the server, all existing sessions will be invalidated.
            </p>
            <button
              onClick={handleRotateJwt}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-all"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Generate New Secret
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 font-medium mb-2">New JWT Secret Generated:</p>
              <code 
                className="block text-xs bg-white px-3 py-2 rounded border border-blue-200 break-all cursor-pointer hover:bg-blue-50"
                onClick={() => copyToClipboard(newJwtSecret)}
              >
                {newJwtSecret}
              </code>
              <p className="text-xs text-blue-600 mt-2">Click to copy</p>
            </div>
            
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <strong>Next Steps:</strong>
              <ol className="list-decimal ml-4 mt-2 space-y-1">
                <li>Copy the secret above</li>
                <li>Update <code className="bg-amber-100 px-1">JWT_SECRET</code> in your backend .env file</li>
                <li>Restart the backend server</li>
                <li>You will need to login again</li>
              </ol>
            </div>
            
            <button
              onClick={() => setShowJwtSecret(false)}
              className="text-sm text-slate-600 hover:text-slate-800"
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* Database Backup & Seed */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Database size={20} className="text-emerald-500" />
          Database Management
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          {/* Backup Section */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
              <Download size={16} />
              Export Backup
            </h4>
            <p className="text-sm text-slate-600 mb-3">
              Download all your portfolio data as a JSON file for safekeeping.
            </p>
            <button
              onClick={handleBackup}
              disabled={backupLoading}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition-all"
              data-testid="backup-btn"
            >
              {backupLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Download Backup
            </button>
          </div>
          
          {/* Seed Section */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
              <Database size={16} />
              Seed Database
            </h4>
            <p className="text-sm text-slate-600 mb-3">
              Populate empty collections with default data (for fresh deployments).
            </p>
            <button
              onClick={handleSeed}
              disabled={seedLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-all"
              data-testid="seed-btn"
            >
              {seedLoading ? <Loader2 size={16} className="animate-spin" /> : <Database size={16} />}
              Seed Data
            </button>
            
            {/* Seed Results */}
            {seedResults && (
              <div className="mt-3 p-3 bg-white rounded border border-slate-200 text-sm">
                <p className="font-medium text-slate-700 mb-2">Seed Results:</p>
                <ul className="space-y-1">
                  {Object.entries(seedResults).map(([key, seeded]) => (
                    <li key={key} className="flex items-center gap-2">
                      {seeded ? (
                        <CheckCircle size={14} className="text-emerald-500" />
                      ) : (
                        <span className="w-3.5 h-3.5 rounded-full bg-slate-300" />
                      )}
                      <span className={seeded ? 'text-emerald-700' : 'text-slate-500'}>
                        {key.replace(/_/g, ' ')}: {seeded ? 'Seeded' : 'Already exists'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Security Recommendations */}
      {settings?.security_recommendations && (
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <AlertCircle size={20} className="text-slate-500" />
            Security Recommendations
          </h3>
          <ul className="space-y-2">
            {settings.security_recommendations.map((rec, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Contact Submissions Manager Component
const ContactSubmissions = ({ token, showToast }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/contacts`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setContacts(data.data || []);
      }
    } catch (err) {
      showToast('Failed to load contacts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'time-sensitive': return 'bg-red-100 text-red-700';
      case 'exciting-collab': return 'bg-purple-100 text-purple-700';
      case 'quick-question': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'time-sensitive': return '🔥 Time Sensitive';
      case 'exciting-collab': return '🚀 Exciting Collab';
      case 'quick-question': return '❓ Quick Question';
      case 'casual-gm': return '👋 Casual GM';
      default: return priority;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Contact Submissions</h2>
          <p className="text-sm text-slate-500">{contacts.length} total submissions (encrypted at rest)</p>
        </div>
        <button
          onClick={fetchContacts}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-all"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Contacts List */}
      {contacts.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center border border-slate-200">
          <Users size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No contact submissions yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div
                className="p-4 cursor-pointer"
                onClick={() => setSelectedContact(selectedContact === index ? null : index)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-800">{contact.name || 'Anonymous'}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(contact.priority)}`}>
                        {getPriorityLabel(contact.priority)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{contact.context}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span>{formatDate(contact.created_at)}</span>
                      {contact.email && <span>📧 {contact.email}</span>}
                      {contact.social && <span>🔗 {contact.social}</span>}
                    </div>
                  </div>
                  <ChevronDown
                    size={20}
                    className={`text-slate-400 transition-transform ${selectedContact === index ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>

              {/* Expanded Details */}
              {selectedContact === index && (
                <div className="px-4 pb-4 border-t border-slate-100 pt-4 bg-slate-50">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Name</label>
                      <p className="text-sm text-slate-800 mt-1">{contact.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Email</label>
                      <p className="text-sm text-slate-800 mt-1">{contact.email || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Social/LinkedIn</label>
                      <p className="text-sm text-slate-800 mt-1">
                        {contact.social ? (
                          <a href={contact.social.startsWith('http') ? contact.social : `https://${contact.social}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {contact.social}
                          </a>
                        ) : 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Priority</label>
                      <p className="text-sm text-slate-800 mt-1">{getPriorityLabel(contact.priority)}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Context</label>
                      <p className="text-sm text-slate-800 mt-1">{contact.context}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Pitch / Message</label>
                      <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{contact.pitch || 'No message'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Admin Panel Component
const AdminPanel = () => {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [activeTab, setActiveTab] = useState('analytics');
  const [toast, setToast] = useState(null);
  const [showSetup2FA, setShowSetup2FA] = useState(false);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setShowSetup2FA(false);
  };

  const handleSetup2FA = (setupToken) => {
    setToken(setupToken);
    setShowSetup2FA(true);
  };

  const handle2FAComplete = (newToken) => {
    setToken(newToken);
    setShowSetup2FA(false);
  };

  // Show 2FA setup screen
  if (showSetup2FA && token) {
    return <TwoFactorSetup token={token} onComplete={handle2FAComplete} showToast={showToast} />;
  }

  // Show login if no token
  if (!token) {
    return <LoginForm onLogin={setToken} onSetup2FA={handleSetup2FA} />;
  }

  const tabs = [
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
    { key: 'contacts', label: 'Contacts', icon: Mail },
    { key: 'timeline', label: 'Timeline', icon: Clock },
    { key: 'investments', label: 'Investments', icon: TrendingUp },
    { key: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
              <LayoutDashboard size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800">Admin Panel</h1>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Shield size={10} className="text-emerald-500" />
                2FA Protected
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all text-sm font-medium"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.key
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
                data-testid={`tab-${tab.key}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {activeTab === 'analytics' && <AnalyticsDashboard token={token} showToast={showToast} />}
        {activeTab === 'contacts' && <ContactSubmissions token={token} showToast={showToast} />}
        {activeTab === 'timeline' && <TimelineManager token={token} showToast={showToast} />}
        {activeTab === 'investments' && <InvestmentsManager token={token} showToast={showToast} />}
        {activeTab === 'settings' && <SettingsManager token={token} showToast={showToast} />}
      </main>
    </div>
  );
};

export default AdminPanel;
